/**
 * The one `returnUrl` validator (02-auth-cutover Tasks 1, 3, 5, 6).
 *
 * Every place a return path is accepted uses this: the login page, the OAuth
 * initiation endpoint, the 401 redirect in `apiFetch` and the page guard. A second
 * implementation is how an open redirect comes back — `startsWith('/')` alone accepts
 * `//host`, `/\host`, `/%2f%2fhost` and a whitespace-prefixed `javascript:`, all of
 * which browsers resolve to another origin.
 *
 * Isomorphic on purpose: no Node and no DOM API, so the browser client and the server
 * routes cannot disagree about what an internal path is.
 */

/** Longest path the app ever produces; anything beyond it is an attack payload. */
const MAX_LENGTH = 2_048;

/** Whitespace and C0/C1 controls. Browsers strip these while resolving a URL, so a
 *  `\t`- or `\n`-prefixed value is a different URL than the one being validated. */
// eslint-disable-next-line no-control-regex
const CONTROL_OR_SPACE = /[\s\u0000-\u001f\u007f-\u009f]/;

/** Any scheme prefix — `javascript:`, `data:`, `https:` — wherever it appears first. */
const SCHEME = /^[A-Za-z][A-Za-z0-9+.-]*:/;

function escapesOrigin(value: string): boolean {
  // `//host` and `/\host` are both protocol-relative to a browser.
  return /^\/[/\\]/.test(value);
}

/**
 * The value when it is an internal, path-relative URL, and `null` otherwise.
 * The value is returned verbatim, never re-encoded: a caller that needs it in a query
 * string encodes it itself.
 */
export function internalReturnUrl(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_LENGTH) return null;
  if (CONTROL_OR_SPACE.test(raw)) return null;
  if (SCHEME.test(raw)) return null;
  if (!raw.startsWith('/') || escapesOrigin(raw)) return null;

  // Percent-encoding is the second door: `/%2f%2fhost` is `//host` by the time a
  // browser resolves it. A value that cannot be decoded at all is refused too.
  //
  // ONE decode, deliberately, because a browser performs one: `/%252f%252fhost` resolves
  // to the literal path `/%2f%2fhost` on this origin, which is not an origin escape.
  // Decoding in a loop until the value stops changing would refuse legitimate paths that
  // merely contain an encoded percent sign.
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return null;
  }
  if (CONTROL_OR_SPACE.test(decoded) || SCHEME.test(decoded) || escapesOrigin(decoded)) return null;

  return raw;
}

/** The validated return path, or the caller's own safe landing page. */
export function safeReturnUrl(raw: unknown, fallback: string): string {
  return internalReturnUrl(raw) ?? fallback;
}

/** The localized login URL a 401 or a page guard sends the browser to. */
export function loginUrl(locale: string, returnUrl?: string | null, extra?: Record<string, string>): string {
  const parameters = new URLSearchParams(extra);
  const internal = internalReturnUrl(returnUrl);
  if (internal) parameters.set('returnUrl', internal);
  const query = parameters.toString();
  return `/${locale}/auth/login${query ? `?${query}` : ''}`;
}
